<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { endpoints, type AttentionItemResponse } from '@/api'

/**
 * What needs doing, on the organizer's home screen (SPEC §8.9, MT-109).
 *
 * The card is quiet when there is nothing outstanding — not "all caught up" in
 * a green box, but absent. A card that is always on screen saying something is
 * furniture, and furniture is not read. The one exception is while the group is
 * still being set up, where the setup steps take the space instead.
 *
 * Every prompt links to the screen that resolves it. A prompt that names a
 * problem and leaves somebody hunting for the fix is a worse version of no
 * prompt: it adds the guilt without the remedy.
 *
 * The headlines are written by the server. "3 families have not answered" and
 * "1 family has not answered" differ by more than an "s" in several of these,
 * and one place for the rule beats four.
 */

const props = defineProps<{ groupId: string }>()

const items = ref<AttentionItemResponse[] | null>(null)
const failed = ref(false)

/** What each kind of prompt looks like, and where it goes. */
const PRESENTATION: Record<string, { icon: string; action: string }> = {
  UnansweredHouseholds: { icon: 'mdi-account-question-outline', action: 'See who' },
  UnclaimedItems: { icon: 'mdi-silverware-variant', action: 'See the menu' },
  SeriesWithoutFutureOccurrence: { icon: 'mdi-calendar-alert', action: 'Open the schedule' },
  HouseholdsAddedSincePublish: { icon: 'mdi-account-plus-outline', action: 'Invite them' },
}

const FALLBACK = { icon: 'mdi-alert-circle-outline', action: 'Open' }

function presentationFor(kind: string) {
  return PRESENTATION[kind] ?? FALLBACK
}

/**
 * Where a prompt sends somebody.
 *
 * A meal-shaped prompt goes to that meal's dashboard, which is where all three
 * of its fixes live — chasing, claiming, and inviting the household that was
 * added late. A series-shaped one goes to the meal list, which is the only
 * place a series is visible today.
 */
function destinationFor(item: AttentionItemResponse) {
  if (item.mealEventId) {
    return {
      name: 'organizer-event',
      params: { groupId: props.groupId, eventId: item.mealEventId },
    }
  }

  return { name: 'organizer-events', params: { groupId: props.groupId } }
}

async function load(): Promise<void> {
  items.value = null
  failed.value = false

  try {
    items.value = (await endpoints.groupAttention(props.groupId)).items
  } catch {
    // Silent on the screen, because this card is an aid rather than the
    // product. A red box where the to-do list should be teaches somebody that
    // something is broken when what is broken is one optional request.
    failed.value = true
    items.value = []
  }
}

onMounted(load)
watch(() => props.groupId, load)

const hasItems = computed(() => (items.value?.length ?? 0) > 0)

defineExpose({ load })
</script>

<template>
  <section v-if="hasItems" class="attention" aria-labelledby="attention-heading">
    <h2 id="attention-heading" class="text-subtitle-1 mb-3">Needs attention</h2>

    <v-card
      v-for="(item, index) in items ?? []"
      :key="`${item.kind}-${item.mealEventId ?? item.eventSeriesId ?? index}`"
      class="pa-4 mb-2"
      :data-testid="`attention-${item.kind}`"
    >
      <div class="attention__row">
        <v-icon
          :icon="presentationFor(item.kind).icon"
          size="22"
          class="attention__icon"
          aria-hidden="true"
        />

        <div class="attention__text">
          <p class="attention__headline">{{ item.headline }}</p>
          <p class="text-body-2 attention__detail">{{ item.detail }}</p>
        </div>

        <v-btn size="small" variant="tonal" :to="destinationFor(item)" class="attention__action">
          {{ presentationFor(item.kind).action }}
        </v-btn>
      </div>
    </v-card>
  </section>
</template>

<style scoped>
.attention {
  margin-bottom: 2rem;
}

.attention__row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.attention__icon {
  margin-top: 0.15rem;
  opacity: 0.7;
}

.attention__text {
  /* Takes the slack so the action sits against the right edge, and wraps under
     on a phone rather than squeezing the text to one word a line. */
  flex: 1 1 14rem;
}

.attention__headline {
  margin: 0;
  font-weight: 600;
}

.attention__detail {
  margin: 0.15rem 0 0;
  opacity: 0.75;
}

.attention__action {
  flex: 0 0 auto;
}
</style>
